import type {
  AdminWalletTransaction,
  ExpertWalletTransaction,
  WalletTransactionDirection,
  WalletTransactionKind,
} from "@shopexperts/legacy-api";
import type { UiIconProps } from "@shopexperts/shared";

/**
 * The display helpers only rely on fields shared by both the expert-facing and
 * admin-facing transaction shapes, so they can be reused across both surfaces.
 */
export type WalletTransactionLike = ExpertWalletTransaction | AdminWalletTransaction;

export type WalletTransactionTone = "green" | "amber" | "red" | "neutral";

interface WalletTransactionKindMeta {
  /** Human readable label for the transaction kind. */
  label: string;
  /** Short explanation of what the transaction represents. */
  description: string;
  /** Icon rendered in the timeline node. */
  icon: UiIconProps["name"];
}

const KIND_META: Record<WalletTransactionKind, WalletTransactionKindMeta> = {
  expert_quote_settlement: {
    label: "Quote payment",
    description: "Earnings from a completed quote, net of fees and commission.",
    icon: "dollar-circle",
  },
  admin_external_payment: {
    label: "External payment",
    description: "A payment collected outside shopexperts and recorded by the shopexperts team.",
    icon: "dollar-circle",
  },
  admin_credit: {
    label: "Credit adjustment",
    description: "A manual credit applied to your wallet by the shopexperts team.",
    icon: "plus-square",
  },
  admin_debit: {
    label: "Debit adjustment",
    description: "A manual debit applied to your wallet by the shopexperts team.",
    icon: "repeat",
  },
  admin_hold: {
    label: "Funds held",
    description: "Funds reserved in your held balance and not yet available to withdraw.",
    icon: "lock",
  },
  admin_hold_debit: {
    label: "Hold debit",
    description: "Funds removed from your held balance by the shopexperts team.",
    icon: "minus",
  },
  admin_gross_sales_credit: {
    label: "Gross sales credit",
    description: "An increase to your lifetime gross sales used for your expert level.",
    icon: "dollar-circle",
  },
  admin_gross_sales_debit: {
    label: "Gross sales debit",
    description: "A decrease to your lifetime gross sales used for your expert level.",
    icon: "dollar-circle",
  },
  admin_net_credit: {
    label: "Net credit",
    description: "An increase to your lifetime net value.",
    icon: "dollar-circle",
  },
  admin_net_debit: {
    label: "Net debit",
    description: "A decrease to your lifetime net value.",
    icon: "dollar-circle",
  },
  hold_release: {
    label: "Hold released",
    description: "Previously held funds were released to your available balance.",
    icon: "check",
  },
  refund: {
    label: "Refund",
    description: "Funds refunded from your available balance.",
    icon: "repeat",
  },
  refund_from_hold: {
    label: "Refund from hold",
    description: "Funds refunded from your held balance.",
    icon: "repeat",
  },
};

const FALLBACK_META: WalletTransactionKindMeta = {
  label: "Transaction",
  description: "Wallet activity.",
  icon: "wallet",
};

export function getTransactionKindMeta(kind: WalletTransactionKind): WalletTransactionKindMeta {
  return KIND_META[kind] ?? FALLBACK_META;
}

/**
 * The net movement of available balance for the expert. We treat the change to
 * the available balance as the headline effect; when a transaction only moves
 * money in or out of hold we fall back to the hold delta so the row still
 * communicates direction.
 */
export function getHeadlineDeltaCents(transaction: WalletTransactionLike): number {
  if (transaction.available_delta_cents !== 0) {
    return transaction.available_delta_cents;
  }
  return transaction.hold_delta_cents;
}

/**
 * The signed amount to feature on the row. Falls back to the gross transaction
 * amount (signed by direction) for ledger entries that don't move the
 * available or hold balances, e.g. gross-sales adjustments.
 */
export function getDisplayAmountCents(transaction: WalletTransactionLike): number {
  const delta = getHeadlineDeltaCents(transaction);
  if (delta !== 0) return delta;
  if (transaction.direction === "debit") return -transaction.amount_cents;
  return transaction.amount_cents;
}

export type WalletMovementId =
  | "into_hold"
  | "released"
  | "credited"
  | "refunded"
  | "debited"
  | "gross_sales"
  | "net"
  | "neutral";

export interface WalletMovement {
  id: WalletMovementId;
  /** Short status label, e.g. "On hold". */
  label: string;
  /** One-line explanation of where the money went. */
  description: string;
  tone: WalletTransactionTone;
  icon: UiIconProps["name"];
}

interface HoldConsumptionAmounts {
  hasAuthoritativeBreakdown: boolean;
  releasedCents: number;
  debitedCents: number;
  refundedCents: number;
}

function getHoldConsumptionAmounts(transaction: WalletTransactionLike): HoldConsumptionAmounts {
  const values = [
    transaction.released_hold_cents,
    transaction.debited_hold_cents,
    transaction.refunded_hold_cents,
  ];

  return {
    hasAuthoritativeBreakdown: values.every((value) => typeof value === "number"),
    releasedCents: Math.max(0, transaction.released_hold_cents ?? 0),
    debitedCents: Math.max(0, transaction.debited_hold_cents ?? 0),
    refundedCents: Math.max(0, transaction.refunded_hold_cents ?? 0),
  };
}

export type WalletTimelineStateId =
  | "hold_active"
  | "hold_cleared"
  | "available_inflow"
  | "wallet_outflow"
  | "gross_sales"
  | "net"
  | "neutral";

export interface WalletTimelineState {
  id: WalletTimelineStateId;
  /** Screen-reader friendly label for the timeline node. */
  label: string;
  tone: WalletTransactionTone;
  icon: UiIconProps["name"];
}

/**
 * Resolves a clear, status-first descriptor for a transaction so the UI can
 * make it obvious whether money was reserved in the held balance, released to
 * the available balance,
 * paid out, or refunded — regardless of the raw transaction kind.
 */
export function getWalletMovement(transaction: WalletTransactionLike): WalletMovement {
  const t = transaction;
  const kind = t.transaction_kind;

  if (kind === "admin_gross_sales_credit" || kind === "admin_gross_sales_debit") {
    return {
      id: "gross_sales",
      label: "Gross sales",
      description: "Adjusts your lifetime gross sales used for your expert level.",
      tone: "neutral",
      icon: "dollar-circle",
    };
  }

  if (kind === "admin_net_credit" || kind === "admin_net_debit") {
    return {
      id: "net",
      label: "Net value",
      description: "Adjusts your lifetime net value only.",
      tone: "neutral",
      icon: "dollar-circle",
    };
  }

  if (
    kind === "admin_external_payment" &&
    t.available_delta_cents === 0 &&
    t.hold_delta_cents === 0
  ) {
    return {
      id: "neutral",
      label: "Paid externally",
      description: "Recorded in lifetime totals without adding funds to your wallet.",
      tone: "neutral",
      icon: "dollar-circle",
    };
  }

  // Money reserved in the held balance (pending) — quote earnings land here first.
  if (t.hold_delta_cents > 0 && t.available_delta_cents <= 0) {
    const consumption = getHoldConsumptionAmounts(t);
    const hasDebit = consumption.debitedCents > 0;
    const hasRefund = consumption.refundedCents > 0;
    const hasRelease = consumption.releasedCents > 0;
    const hasNonReleaseConsumption = consumption.hasAuthoritativeBreakdown && (hasDebit || hasRefund);

    if (t.remaining_hold_cents === 0) {
      if (hasNonReleaseConsumption) {
        const debitOnly = hasDebit && !hasRefund && !hasRelease;
        const refundOnly = hasRefund && !hasDebit && !hasRelease;
        return {
          id: debitOnly ? "debited" : refundOnly ? "refunded" : "neutral",
          label: debitOnly ? "Fully debited" : refundOnly ? "Fully refunded" : "Fully consumed",
          description: debitOnly
            ? "This hold was fully removed by an admin debit."
            : refundOnly
              ? "This hold was fully refunded from the held balance."
              : "This hold was fully consumed across releases, debits, or refunds.",
          tone: debitOnly || refundOnly ? "red" : "neutral",
          icon: debitOnly ? "minus" : refundOnly ? "repeat" : "lock-open",
        };
      }

      return {
        id: "released",
        label: "Fully released",
        description: "This hold has been fully released to your available balance.",
        tone: "green",
        icon: "lock-open",
      };
    }

    const originalHoldCents = Math.max(
      t.hold_delta_cents,
      t.net_amount_cents ?? 0,
      t.remaining_hold_cents ?? 0,
    );
    const partiallyReleased =
      t.remaining_hold_cents !== null &&
      t.remaining_hold_cents > 0 &&
      t.remaining_hold_cents < originalHoldCents;

    if (partiallyReleased) {
      if (hasNonReleaseConsumption) {
        const debitOnly = hasDebit && !hasRefund && !hasRelease;
        const refundOnly = hasRefund && !hasDebit && !hasRelease;
        return {
          id: debitOnly ? "debited" : refundOnly ? "refunded" : "neutral",
          label: debitOnly
            ? "Partially debited"
            : refundOnly
              ? "Partially refunded"
              : "Partially consumed",
          description: debitOnly
            ? "Part of this hold was removed by an admin debit; the rest remains on hold."
            : refundOnly
              ? "Part of this hold was refunded; the rest remains on hold."
              : "Part of this hold was released, debited, or refunded; the rest remains on hold.",
          tone: "amber",
          icon: "lock",
        };
      }

      return {
        id: "released",
        label: "Partially released",
        description: "Part of this hold has been released; the rest remains on hold.",
        tone: "amber",
        icon: "lock",
      };
    }

    return {
      id: "into_hold",
      label: "On hold",
      description:
        kind === "expert_quote_settlement" || kind === "admin_external_payment"
          ? "Client payment is reserved in your held balance until it is released to your available balance."
          : "Funds reserved in your held balance and not yet available to withdraw.",
      tone: "amber",
      icon: "lock",
    };
  }

  // Held funds released to the available balance.
  if (kind === "hold_release" || (t.hold_delta_cents < 0 && t.available_delta_cents > 0)) {
    return {
      id: "released",
      label: "Released from hold",
      description: "Held funds were released to your available balance.",
      tone: "green",
      icon: "check",
    };
  }

  // Added directly to the available balance.
  if (t.available_delta_cents > 0) {
    return {
      id: "credited",
      label: "Available",
      description: "Added to your available balance.",
      tone: "green",
      icon: "plus-square",
    };
  }

  if (kind === "refund" || kind === "refund_from_hold") {
    return {
      id: "refunded",
      label: "Refunded",
      description:
        kind === "refund_from_hold"
          ? "Refunded from your held balance."
          : "Refunded from your available balance.",
      tone: "red",
      icon: "repeat",
    };
  }

  if (kind === "admin_hold_debit") {
    return {
      id: "debited",
      label: "Hold debit",
      description: getTransactionKindMeta(kind).description,
      tone: "red",
      icon: "minus",
    };
  }

  // Any remaining outflow.
  if (t.available_delta_cents < 0 || t.hold_delta_cents < 0) {
    return {
      id: "debited",
      label: "Deducted",
      description: "Deducted from your wallet balance.",
      tone: "red",
      icon: "repeat",
    };
  }

  return {
    id: "neutral",
    label: getDirectionLabel(t.direction),
    description: getTransactionKindMeta(kind).description,
    tone: "neutral",
    icon: getTransactionKindMeta(kind).icon,
  };
}

/**
 * The timeline rail represents the action recorded by this ledger row. Later
 * follow-up releases should update the amount/status area, not rewrite the
 * historical icon on the original hold transaction.
 */
export function getWalletTimelineState(transaction: WalletTransactionLike): WalletTimelineState {
  const t = transaction;
  const kind = t.transaction_kind;

  if (kind === "admin_gross_sales_credit" || kind === "admin_gross_sales_debit") {
    return {
      id: "gross_sales",
      label: "Gross sales adjusted",
      tone: "neutral",
      icon: "dollar-circle",
    };
  }

  if (kind === "admin_net_credit" || kind === "admin_net_debit") {
    return {
      id: "net",
      label: "Net value adjusted",
      tone: "neutral",
      icon: "dollar-circle",
    };
  }

  if (
    kind === "admin_external_payment" &&
    t.available_delta_cents === 0 &&
    t.hold_delta_cents === 0
  ) {
    return {
      id: "neutral",
      label: "External payment recorded",
      tone: "neutral",
      icon: "dollar-circle",
    };
  }

  if (kind === "hold_release" || (t.hold_delta_cents < 0 && t.available_delta_cents > 0)) {
    return {
      id: "hold_cleared",
      label: "Released from hold",
      tone: "green",
      icon: "lock-open",
    };
  }

  if (t.hold_delta_cents > 0) {
    return {
      id: "hold_active",
      label: "Funds placed on hold",
      tone: "amber",
      icon: "lock",
    };
  }

  if (kind === "refund" || kind === "refund_from_hold" || t.available_delta_cents < 0 || t.hold_delta_cents < 0) {
    return {
      id: "wallet_outflow",
      label: kind === "refund" || kind === "refund_from_hold" ? "Funds refunded" : "Wallet debited",
      tone: "red",
      icon: "minus",
    };
  }

  if (t.available_delta_cents > 0 || t.direction === "credit") {
    return {
      id: "available_inflow",
      label: "Wallet credited",
      tone: "green",
      icon: "plus",
    };
  }

  return {
    id: "neutral",
    label: "Wallet transaction",
    tone: "neutral",
    icon: "wallet",
  };
}

const TONE_NODE_CLASSES: Record<WalletTransactionTone, string> = {
  green: "bg-se-success-light text-se-green border-se-success",
  amber: "bg-se-warn-light text-se-warn border-se-warn",
  red: "bg-red-50 text-red-600 border-red-200",
  neutral: "bg-se-gray-100 text-se-gray-700 border-se-gray-300",
};

const TONE_AMOUNT_CLASSES: Record<WalletTransactionTone, string> = {
  green: "text-se-green",
  amber: "text-se-warn",
  red: "text-red-600",
  neutral: "text-se-dark",
};

const TONE_PILL_CLASSES: Record<WalletTransactionTone, string> = {
  green: "bg-se-success-light text-se-green",
  amber: "bg-se-warn-light text-se-warn",
  red: "bg-red-50 text-red-600",
  neutral: "bg-se-gray-100 text-se-gray-600",
};

export function getToneNodeClasses(tone: WalletTransactionTone): string {
  return TONE_NODE_CLASSES[tone];
}

export function getToneAmountClasses(tone: WalletTransactionTone): string {
  return TONE_AMOUNT_CLASSES[tone];
}

export function getTonePillClasses(tone: WalletTransactionTone): string {
  return TONE_PILL_CLASSES[tone];
}

export function getDirectionLabel(direction: WalletTransactionDirection): string {
  switch (direction) {
    case "credit":
      return "Credit";
    case "debit":
      return "Debit";
    case "transfer":
      return "Transfer";
    default:
      return direction;
  }
}

export function getWalletTransactionHeadline(transaction: WalletTransactionLike): string {
  const kind = transaction.transaction_kind;
  switch (kind) {
    case "expert_quote_settlement":
      return "Client paid for a quote";
    case "admin_external_payment":
      return transaction.quote_id === null
        ? "External payment was recorded"
        : "Client paid externally for a quote";
    case "admin_credit":
      return "Wallet credit was added";
    case "admin_debit":
      return "Wallet balance was debited";
    case "admin_hold":
      return transaction.available_delta_cents < 0
        ? "Funds were moved to hold"
        : "Funds were placed on hold";
    case "admin_hold_debit":
      return "Held funds were debited";
    case "admin_gross_sales_credit":
      return "Gross sales were credited";
    case "admin_gross_sales_debit":
      return "Gross sales were debited";
    case "admin_net_credit":
      return "Net value was credited";
    case "admin_net_debit":
      return "Net value was debited";
    case "hold_release":
      return "Held funds were released";
    case "refund":
      return "Funds were refunded";
    case "refund_from_hold":
      return "Held funds were refunded";
    default:
      return getTransactionKindMeta(kind).description.replace(/\.$/, "");
  }
}

/**
 * Formats a signed amount string like `+$120.00` / `-$15.00`. A zero delta is
 * rendered without a sign.
 */
export function formatSignedCurrency(
  cents: number,
  currency: string | null,
  formatCurrency: (cents: number, currency?: string | null) => string,
): string {
  const formatted = formatCurrency(Math.abs(cents), currency);
  if (cents > 0) return `+${formatted}`;
  if (cents < 0) return `-${formatted}`;
  return formatted;
}

export interface WalletHeadlineAmount {
  label: string;
  icon: UiIconProps["name"] | null;
}

export function getHeadlineAmount(
  transaction: WalletTransactionLike,
  formatCurrency: (cents: number, currency?: string | null) => string,
): WalletHeadlineAmount {
  const amountCents = getDisplayAmountCents(transaction);
  const label = formatCurrency(Math.abs(amountCents), transaction.currency);

  if (transaction.hold_delta_cents > 0 && transaction.available_delta_cents <= 0) {
    const consumption = getHoldConsumptionAmounts(transaction);
    const fullyConsumed = transaction.remaining_hold_cents === 0;
    const debitOnly =
      fullyConsumed &&
      consumption.hasAuthoritativeBreakdown &&
      consumption.debitedCents > 0 &&
      consumption.releasedCents === 0 &&
      consumption.refundedCents === 0;
    const refundOnly =
      fullyConsumed &&
      consumption.hasAuthoritativeBreakdown &&
      consumption.refundedCents > 0 &&
      consumption.releasedCents === 0 &&
      consumption.debitedCents === 0;

    return {
      label,
      icon: debitOnly ? "minus" : refundOnly ? "repeat" : fullyConsumed ? "lock-open" : "lock",
    };
  }

  if (
    transaction.transaction_kind === "admin_hold" &&
    transaction.available_delta_cents < 0 &&
    transaction.hold_delta_cents > 0
  ) {
    return {
      label,
      icon: "lock",
    };
  }

  if (transaction.transaction_kind === "hold_release") {
    return {
      label,
      icon: "lock-open",
    };
  }

  if (
    transaction.transaction_kind === "admin_external_payment" &&
    transaction.available_delta_cents === 0 &&
    transaction.hold_delta_cents === 0
  ) {
    return {
      label,
      icon: "dollar-circle",
    };
  }

  if (amountCents > 0) {
    return {
      label,
      icon: "plus",
    };
  }

  if (amountCents < 0) {
    return {
      label,
      icon: "minus",
    };
  }

  return {
    label,
    icon: null,
  };
}

export interface WalletEffectAmount {
  availableCents: number;
  holdCents: number;
  refundedCents: number;
  debitedCents: number;
  showSignedHold: boolean;
}

export function getReleaseTransactionIds(transaction: WalletTransactionLike): number[] {
  return [...new Set(transaction.release_transaction_ids ?? [])];
}

/**
 * The API exposes raw ledger deltas and, for hold-backed transactions, the hold
 * still remaining after that row. When the remaining hold is available, show
 * the card as the current split: released to the available balance vs still in
 * the held balance.
 */
export function getWalletEffectAmount(transaction: WalletTransactionLike): WalletEffectAmount {
  const consumption = getHoldConsumptionAmounts(transaction);

  if (
    transaction.transaction_kind === "admin_external_payment" &&
    transaction.hold_delta_cents <= 0
  ) {
    return {
      availableCents: transaction.available_delta_cents,
      holdCents: transaction.hold_delta_cents,
      refundedCents: 0,
      debitedCents: 0,
      showSignedHold: true,
    };
  }

  if (transaction.remaining_hold_cents === null) {
    return {
      availableCents: transaction.available_delta_cents,
      holdCents: transaction.hold_delta_cents,
      refundedCents: 0,
      debitedCents: 0,
      showSignedHold: true,
    };
  }

  if (consumption.hasAuthoritativeBreakdown) {
    return {
      availableCents: consumption.releasedCents,
      holdCents: transaction.remaining_hold_cents,
      refundedCents: consumption.refundedCents,
      debitedCents: consumption.debitedCents,
      showSignedHold: false,
    };
  }

  if (transaction.available_delta_cents < 0 && transaction.hold_delta_cents > 0) {
    return {
      availableCents: transaction.remaining_hold_cents === 0 ? 0 : -transaction.remaining_hold_cents,
      holdCents: transaction.remaining_hold_cents,
      refundedCents: 0,
      debitedCents: 0,
      showSignedHold: false,
    };
  }

  const originalHoldCents =
    transaction.available_delta_cents > 0 && transaction.hold_delta_cents < 0
      ? transaction.remaining_hold_cents + transaction.available_delta_cents
      : Math.max(
          transaction.remaining_hold_cents,
          transaction.hold_delta_cents,
          transaction.net_amount_cents ?? 0,
        );

  return {
    availableCents: Math.max(0, originalHoldCents - transaction.remaining_hold_cents),
    holdCents: transaction.remaining_hold_cents,
    refundedCents: 0,
    debitedCents: 0,
    showSignedHold: false,
  };
}

/** Basis points (e.g. 1500) to a percentage label (e.g. `15%`). */
export function formatBps(bps: number): string {
  const percent = bps / 100;
  return `${Number.isInteger(percent) ? percent : percent.toFixed(2)}%`;
}

export function parseExpertLevelCommissionRateBps(expertLevel: unknown): number | null {
  if (expertLevel == null || typeof expertLevel !== "object") return null;
  const bps = (expertLevel as Record<string, unknown>).commission_rate_bps;
  return typeof bps === "number" && Number.isFinite(bps) ? bps : null;
}

export function buildCommissionTooltip(
  perspective: "self" | "admin",
  commissionRateBps: number | null | undefined,
): string {
  if (commissionRateBps == null || !Number.isFinite(commissionRateBps)) {
    return perspective === "self"
      ? "shopexperts commission based on your current expert level."
      : "shopexperts commission based on the expert's level at settlement.";
  }

  const rate = formatBps(commissionRateBps);
  return perspective === "self"
    ? `shopexperts commission at ${rate} based on your current expert level.`
    : `shopexperts commission at ${rate} based on the expert's level at settlement.`;
}

export interface ProviderFeeDetailLine {
  key: string;
  /** Human readable description for the fee line. */
  label: string;
  /** Fee amount in cents, when available. */
  amountCents: number | null;
  /** ISO currency code for the line, when available. */
  currency: string | null;
}

function humanizeFeeType(type: string): string {
  const cleaned = type.replace(/[_-]+/g, " ").trim();
  if (!cleaned) return "Processing fee";
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function toCents(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function toText(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

/**
 * Normalizes the provider's raw fee breakdown (e.g. Stripe `fee_details`) into
 * renderable lines. Tolerates missing fields and unexpected shapes so the UI
 * never breaks on partial data.
 */
export function parseProviderFeeDetails(value: unknown): ProviderFeeDetailLine[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry, index): ProviderFeeDetailLine | null => {
      if (entry == null || typeof entry !== "object") return null;
      const record = entry as Record<string, unknown>;

      const label =
        toText(record.description) ??
        (toText(record.type) ? humanizeFeeType(record.type as string) : null) ??
        "Processing fee";

      return {
        key: `${index}-${label}`,
        label,
        amountCents: toCents(record.amount),
        currency: toText(record.currency)?.toUpperCase() ?? null,
      };
    })
    .filter((line): line is ProviderFeeDetailLine => line !== null);
}
